import { useAppData } from "@/contexts/AppDataContext";
import { useAgents } from "@/hooks/useAgents";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import * as React from "react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { CallQueueType } from "@/constants/queue";

interface DirectAgentFilterProps {
  value: string | string[];
  filterType: CallQueueType;
  onChange: (value: string[], filterType: CallQueueType) => void;
  placeholder?: string;
  className?: string;
  agents?: { microsoftId: string; name: string }[];
}

export const DirectAgentFilter: React.FC<DirectAgentFilterProps> = ({
  value,
  filterType,
  onChange,
  className,
  agents: agentsProp
}) => {
  const allDirectAgentGroupLabel = "All Groups";
  const allAgentsLabel = "All Agents";
  const { directAgentGroups: directAgentGroupsFromContext } = useAppData();
  const { agents: agentsFromStatus } = useAgents();
  const agents = agentsProp ?? agentsFromStatus;
  // Defensive filter: the Group section must never show Call Queues (FR-002),
  // even if a caller's directAgentGroups source isn't pre-filtered by type.
  const directAgentGroups = React.useMemo(
    () => directAgentGroupsFromContext.filter(g => g.type === CallQueueType.DIRECT_AGENT_GROUP),
    [directAgentGroupsFromContext]
  );

  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedFilterType, setSelectedFilterType] = React.useState<CallQueueType>(filterType);
  const [collapsedSections, setCollapsedSections] = React.useState<{
    groups: boolean;
    agents: boolean;
  }>({
    groups: true,
    agents: true
  });

  // Ensure value is always an array
  const selectedValues = React.useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Filter data based on search term
  const filteredData = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return {
        directAgentGroups: directAgentGroups,
        agents: agents
      };
    }

    const searchLower = searchTerm.toLowerCase().trim();

    const filteredDirectAgentGroups = directAgentGroups.filter(group =>
      group.label.toLowerCase().includes(searchLower)
    );

    const filteredAgents = agents.filter(agent =>
      agent.name.toLowerCase().includes(searchLower)
    );

    return {
      directAgentGroups: filteredDirectAgentGroups,
      agents: filteredAgents
    };
  }, [directAgentGroups, agents, searchTerm]);

  const handleValueToggle = (microsoftId: string, filterType: CallQueueType) => {
    setSelectedFilterType(filterType);
    if (microsoftId === "") {
      onChange([], filterType);
      return;
    }

    // Check if the selected item is a group or agent
    const isGroup = directAgentGroups.some(g => g.microsoftId === microsoftId);
    const isAgent = agents.some(a => a.microsoftId === microsoftId);

    if (isGroup) {
      // If selecting a group, clear all agents and toggle the group
      const newValue = selectedValues.includes(microsoftId)
        ? selectedValues.filter((v) => v !== microsoftId && v !== "")
        : [...selectedValues.filter((v) => v !== ""), microsoftId];

      // Remove all agent selections
      const finalValue = newValue.filter(v =>
        directAgentGroups.some(g => g.microsoftId === v) || v === ""
      );

      onChange(finalValue.length === 0 ? [] : finalValue, filterType);
    } else if (isAgent) {
      // If selecting an agent, clear all groups and toggle the agent
      const newValue = selectedValues.includes(microsoftId)
        ? selectedValues.filter((v) => v !== microsoftId && v !== "")
        : [...selectedValues.filter((v) => v !== ""), microsoftId];

      // Remove all group selections
      const finalValue = newValue.filter(v =>
        agents.some(a => a.microsoftId === v) || v === ""
      );
      onChange(finalValue.length === 0 ? [] : finalValue, filterType);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setOpen(false);
      setSearchTerm("");
    } else {
      setOpen(true);
    }
  };

  const handleGroupClick = (group: 'groups' | 'agents') => {
    const newFilterType = group === 'groups' ? CallQueueType.DIRECT_AGENT_GROUP : CallQueueType.AGENT;

    // If clicking on the same active section, toggle collapse
    if (selectedFilterType === newFilterType) {
      setCollapsedSections(prev => ({
        ...prev,
        [group]: !prev[group]
      }));
      return;
    }

    // Switch to new section and auto-expand it
    setSelectedFilterType(newFilterType);
    setCollapsedSections(prev => ({
      ...prev,
      [group]: false
    }));

    // Send onChange event when switching groups
    // Clear current selections and switch to new group type
    onChange([], newFilterType);
  };

  const toggleSectionCollapse = (section: 'groups' | 'agents') => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Auto-switch to available group if current group is empty (only when not searching)
  React.useEffect(() => {
    // Don't auto-switch when user is actively searching
    if (searchTerm.trim()) return;

    if (selectedFilterType === CallQueueType.DIRECT_AGENT_GROUP && directAgentGroups.length === 0 && agents.length > 0) {
      setSelectedFilterType(CallQueueType.AGENT);
    } else if (selectedFilterType === CallQueueType.AGENT && agents.length === 0 && directAgentGroups.length > 0) {
      setSelectedFilterType(CallQueueType.DIRECT_AGENT_GROUP);
    }
  }, [selectedFilterType, directAgentGroups.length, agents.length, searchTerm]);

  // Auto-expand sections when searching to show results
  React.useEffect(() => {
    if (searchTerm.trim()) {
      setCollapsedSections({
        groups: false,
        agents: false
      });
    }
  }, [searchTerm]);

  // Auto-reset to "All" if selected agent is removed from the list
  React.useEffect(() => {
    if (!selectedValues || selectedValues.length === 0) {
      return; // Already showing "All"
    }

    // Check if any selected value is removed
    const availableIds = [
      ...directAgentGroups.map(g => g.microsoftId),
      ...agents.map(a => a.microsoftId)
    ];

    const hasRemovedItem = selectedValues.some(id => id !== "" && !availableIds.includes(id));

    if (hasRemovedItem) {
      console.log('DirectAgentFilter: Detected removed item in selection, resetting to All');
      onChange([], selectedFilterType); // Reset to "All"
    }
  }, [directAgentGroups, agents, selectedValues, selectedFilterType, onChange]);

  // Get selected labels for display
  const selectedLabels = React.useMemo(() => {
    const labels: string[] = [];

    selectedValues.forEach(value => {
      if (value === "") return;

      // Check in direct agent groups
      const directAgentGroup = directAgentGroups.find(g => g.microsoftId === value);
      if (directAgentGroup) {
        labels.push(directAgentGroup.label);
        return;
      }

      // Check in agents
      const agent = agents.find(a => a.microsoftId === value);
      if (agent) {
        labels.push(agent.name);
        return;
      }
    });

    return labels.join(", ");
  }, [selectedValues, directAgentGroups, agents]);

  const isGroupsExpanded = (searchTerm.trim() !== "" || selectedFilterType === CallQueueType.DIRECT_AGENT_GROUP) && !collapsedSections.groups;
  const isAgentsExpanded = (searchTerm.trim() !== "" || selectedFilterType === CallQueueType.AGENT) && !collapsedSections.agents;

  const getPlaceholder = () => {
    if (selectedValues.length === 0) {
      return selectedFilterType === CallQueueType.DIRECT_AGENT_GROUP ? allDirectAgentGroupLabel : allAgentsLabel;
    }
    return selectedLabels;
  };

  return (
    <div className="flex items-center gap-2 w-full min-w-[180px] md:w-[230px]">
      <DropdownMenu open={open} onOpenChange={handleOpenChange} modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "relative flex items-center w-full bg-[#F5F6F7] dark:bg-[#0c3451] rounded-xl px-4 py-1.5 font-bold text-primary dark:text-white text-sm focus:outline-none border-0 shadow-none h-[36px] hover:bg-[#e9eaeb] dark:hover:bg-[#2C4C63] transition justify-between",
              className
            )}
            style={{ boxShadow: "none" }}
          >
            <span className="truncate text-left text-sm w-full pr-6 text-primary dark:text-white">
              {getPlaceholder()}
            </span>
            <ChevronDown className="w-4 h-4 text-primary dark:text-white absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="max-w-[400px] p-0 text-sm bg-white dark:bg-[#3C5C73] max-h-[400px] overflow-hidden" align="start">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm bg-gray-50 dark:bg-[#3C5C73] border-gray-200 dark:border-white text-primary dark:text-white placeholder-gray-500"
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Scrollable Options */}
          <div className="max-h-[300px] overflow-y-auto">
            <div className="p-2">
              {/* Groups Section */}
              {filteredData.directAgentGroups.length > 0 && (
                <>
                  <button
                    type="button"
                    className={cn(
                      "w-full text-left bg-transparent border-0 px-3 py-2 text-xs font-semibold uppercase tracking-wide flex items-center gap-2 cursor-pointer transition-colors",
                      selectedFilterType === CallQueueType.DIRECT_AGENT_GROUP
                        ? "text-[#00FEC1] dark:text-[#00FEC1]"
                        : "text-gray-600 dark:text-gray-400 hover:text-[#00FEC1] dark:hover:text-[#00FEC1]"
                    )}
                    onClick={() => handleGroupClick('groups')}
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        // If clicking on icon of active section, toggle collapse
                        if (selectedFilterType === CallQueueType.DIRECT_AGENT_GROUP) {
                          toggleSectionCollapse('groups');
                        } else {
                          // If clicking on icon of inactive section, switch to that section and expand
                          handleGroupClick('groups');
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {isGroupsExpanded ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                    </div>
                    Group
                  </button>

                  {/* Show groups when searching OR when groups are selected AND not collapsed */}
                  {isGroupsExpanded && (
                    <>
                      {/* All Direct Agents option inside Groups - only show when not searching */}
                      {!searchTerm.trim() && (
                        <DropdownMenuCheckboxItem
                          key="all-direct-agents"
                          checked={selectedValues?.length === 0 && selectedFilterType === CallQueueType.DIRECT_AGENT_GROUP}
                          onCheckedChange={() => handleValueToggle("", CallQueueType.DIRECT_AGENT_GROUP)}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                          className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer font-medium ml-4"
                        >
                          {allDirectAgentGroupLabel}
                        </DropdownMenuCheckboxItem>
                      )}
                      {filteredData.directAgentGroups.map((group) => (
                        <DropdownMenuCheckboxItem
                          key={`direct-agent-group-${group.microsoftId}`}
                          checked={selectedValues.includes(group.microsoftId)}
                          onCheckedChange={() => handleValueToggle(group.microsoftId, CallQueueType.DIRECT_AGENT_GROUP)}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                          className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer ml-4"
                        >
                          {group.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </>
                  )}
                </>
              )}

              {/* Agents Section */}
              {filteredData.agents.length > 0 && (
                <>
                  <button
                    type="button"
                    className={cn(
                      "w-full text-left bg-transparent border-0 px-3 py-2 text-xs font-semibold uppercase tracking-wide flex items-center gap-2 cursor-pointer transition-colors",
                      selectedFilterType === CallQueueType.AGENT
                        ? "text-[#00FEC1] dark:text-[#00FEC1]"
                        : "text-gray-600 dark:text-gray-400 hover:text-[#00FEC1] dark:hover:text-[#00FEC1]"
                    )}
                    onClick={() => handleGroupClick('agents')}
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        // If clicking on icon of active section, toggle collapse
                        if (selectedFilterType === CallQueueType.AGENT) {
                          toggleSectionCollapse('agents');
                        } else {
                          // If clicking on icon of inactive section, switch to that section and expand
                          handleGroupClick('agents');
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {isAgentsExpanded ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                    </div>
                    Agent
                  </button>

                  {/* Show agents when searching OR when agents are selected AND not collapsed */}
                  {isAgentsExpanded && (
                    <>
                      {/* All Agents option inside Agents - only show when not searching */}
                      {!searchTerm.trim() && (
                        <DropdownMenuCheckboxItem
                          key="all-agents"
                          checked={selectedValues?.length === 0 && selectedFilterType === CallQueueType.AGENT}
                          onCheckedChange={() => handleValueToggle("", CallQueueType.AGENT)}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                          className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer font-medium ml-4"
                        >
                          {allAgentsLabel}
                        </DropdownMenuCheckboxItem>
                      )}
                      {filteredData.agents.map((agent, index) => (
                        <DropdownMenuCheckboxItem
                          key={`agent-${agent.microsoftId}-${index}`}
                          checked={selectedValues.includes(agent.microsoftId)}
                          onCheckedChange={() => handleValueToggle(agent.microsoftId, CallQueueType.AGENT)}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                            className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer ml-4"
                        >
                          {agent.name}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </>
                  )}
                </>
              )}

              {/* No results message - show when searching and no results found */}
              {searchTerm.trim() && filteredData.directAgentGroups.length === 0 && filteredData.agents.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No results found
                </div>
              )}
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
